/**
 * Audit-Log-Viewer (Spec 11). Zeigt die letzten Eintraege des aktiven
 * Mandanten; Filter kommen mit den Admin-Bereichen in Phase 1.
 */
import type { AuditLogEntry, Paginated } from '@tickethub/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';
import { apiPaths } from '@/lib/api-paths';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';

export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const { activeMembership } = useSession();
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const tenantId = activeMembership?.tenant_id;

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    void (async () => {
      try {
        const page = await api.get<Paginated<AuditLogEntry>>(
          `${apiPaths.auditLog(tenantId)}?page=1&per_page=50`,
        );
        if (!cancelled) setEntries(page.items);
      } catch (error) {
        if (!cancelled) {
          setEntries([]);
          toastError(error, t);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, t]);

  // Datum immer ueber den Locale-Formatter (i18n.md), nie manuell zusammenbauen.
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('audit.title')}</h1>

      {entries === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : entries.length === 0 ? (
        <Alert>
          <AlertDescription>{t('audit.empty')}</AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('audit.time')}</th>
                <th className="px-4 py-2 font-medium">{t('audit.action')}</th>
                <th className="px-4 py-2 font-medium">{t('audit.entity')}</th>
                <th className="px-4 py-2 font-medium">{t('audit.actor')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-t">
                  <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                    {formatter.format(new Date(entry.created_at))}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{entry.action}</td>
                  <td className="px-4 py-2 text-muted-foreground">{entry.entity_type}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                    {entry.actor}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
